import React, { useEffect, useMemo, useRef, useState } from "react";
import { RSD, imgURL } from "../../lib/api";
import { Minus, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../context/I18nContext";
import { L } from "../../lib/localize";
import { effectiveBase } from "../../lib/price";
import { track } from "../../lib/analytics";

// Selection-rule label for an option group (e.g. "Obavezno · Izaberi 1", "Opciono · Do 3").
function ruleLabel(g, t) {
  const status = g.required ? t("menu.required", "Obavezno") : t("menu.optional", "Opciono");
  let count = "";
  if (g.type === "single") {
    count = `${t("menu.choose_verb", "Izaberi")} 1`;
  } else {
    const min = Math.max(Number(g.min_selection || 0), g.required ? 1 : 0);
    const max = Number(g.max_selection || 0);
    if (max && min === max) count = `${t("menu.choose_verb", "Izaberi")} ${max}`;
    else if (max) count = `${t("menu.up_to", "Do")} ${max}`;
    else if (min) count = `${t("menu.at_least", "Najmanje")} ${min}`;
  }
  return count ? `${status} · ${count}` : status;
}

export default function ProductDrawer({ product, onClose, restaurant, onAdd }) {
  const { t, locale } = useI18n();
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [person, setPerson] = useState("");
  const [errorGroup, setErrorGroup] = useState(null);
  const groupRefs = useRef({});

  useEffect(() => {
    if (product) {
      const init = {};
      (product.modifier_groups || []).forEach((g) => {
        if (g.required && g.type === "single") {
          const first = (g.options || []).find((o) => o.available !== false);
          init[g.id] = first ? [first.id] : [];
        } else init[g.id] = [];
      });
      setSelected(init);
      setQty(1);
      setNote("");
      setPerson("");
      setErrorGroup(null);
    }
  }, [product]);

  // Robust background scroll lock (iOS Safari safe): freeze <body> at the current
  // scroll position while the sheet is open, and restore it exactly on close.
  useEffect(() => {
    if (!product) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position, top: body.style.top, left: body.style.left,
      right: body.style.right, width: body.style.width, overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [product]);

  const toggle = (group, optId) => {
    setErrorGroup(null);
    // First interaction with a product's option groups (once per product per session).
    track("product_option_opened", { restaurant_id: restaurant?.id, product_id: product?.id,
                                     category_id: product?.category_id, group_id: group.id,
                                     dedupe_key: `${product?.id}:${group.id}` });
    setSelected((prev) => {
      const cur = prev[group.id] || [];
      if (group.type === "single") return { ...prev, [group.id]: [optId] };
      const max = Number(group.max_selection || 0);
      if (cur.includes(optId)) return { ...prev, [group.id]: cur.filter((x) => x !== optId) };
      if (max && cur.length >= max) {
        toast.warning(`${L(group, locale)}: ${t("menu.up_to", "Do")} ${max}`);
        return prev;
      }
      return { ...prev, [group.id]: [...cur, optId] };
    });
  };
  const toggleExtra = (optId) => {
    setSelected((prev) => {
      const cur = prev.__extras || [];
      return { ...prev, __extras: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
    });
  };

  const allIds = useMemo(() => [
    ...Object.entries(selected).filter(([k]) => k !== "__extras").flatMap(([, v]) => v),
    ...(selected.__extras || []),
  ], [selected]);

  const { unit, selectedOpts } = useMemo(() => {
    if (!product) return { unit: 0, selectedOpts: [] };
    const priceAll = [...(product.modifier_groups || []).flatMap((g) => g.options || []), ...(product.extras || [])];
    const extra = allIds.reduce((s, id) => s + (priceAll.find((o) => o.id === id)?.price || 0), 0);
    const opts = [];
    for (const g of product.modifier_groups || [])
      for (const o of g.options || []) if ((selected[g.id] || []).includes(o.id)) opts.push(o);
    for (const o of product.extras || []) if ((selected.__extras || []).includes(o.id)) opts.push(o);
    return { unit: effectiveBase(product) + extra, selectedOpts: opts };
  }, [product, allIds, selected]);

  if (!product) return null;

  const impossible = (product.modifier_groups || []).some((g) => g.required && !(g.options || []).some((o) => o.available !== false));

  const add = () => {
    for (const g of product.modifier_groups || []) {
      const hasAvail = (g.options || []).some((o) => o.available !== false);
      if (g.required && !hasAvail) { toast.error(t("menu.cannot_order_options", "Proizvod trenutno nije moguće poručiti zbog nedostupnih opcija")); return; }
      const min = g.type === "single" ? (g.required ? 1 : 0) : Math.max(Number(g.min_selection || 0), g.required ? 1 : 0);
      if (min > 0 && (selected[g.id] || []).length < min) {
        setErrorGroup(g.id);
        groupRefs.current[g.id]?.scrollIntoView({ behavior: "smooth", block: "center" });
        toast.error(`${t("menu.choose", "Izaberite:")} ${L(g, locale)}`);
        return;
      }
    }
    const ok = onAdd(product, allIds, qty, note, person.trim());
    if (ok) { toast.success(t("menu.added_to_cart", "Dodato u korpu")); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 max-w-md mx-auto" data-testid="product-drawer" role="dialog" aria-modal="true">
      {/* Dimmed backdrop — intercepts touches/clicks; does NOT close (X only). */}
      <div className="absolute inset-0 bg-black/50" data-testid="product-backdrop" />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[92vh] flex flex-col animate-slide-up">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="relative">
            {product.image
              ? <img src={imgURL(product.image)} alt={L(product, locale)} className="w-full aspect-[4/3] object-cover" />
              : <div className="w-full aspect-[4/3] bg-gradient-to-br from-brand-yellow to-brand-orange" />}
            <button onClick={onClose} data-testid="close-product"
              aria-label={t("common.close", "Zatvori")}
              className="absolute top-3 right-3 bg-white rounded-full p-2.5 shadow active:scale-95 transition-transform">
              <X size={18} />
            </button>
          </div>
          <div className="p-5">
            <h2 className="font-display font-black text-2xl">{L(product, locale)}</h2>
            {L(product, locale, "description") && <p className="text-slate-500 text-sm mt-1">{L(product, locale, "description")}</p>}
            {product.happy_hour ? (
              <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="drawer-price-promo">
                <span className="line-through text-red-500 text-sm font-bold">{RSD(product.happy_hour.base_price)}</span>
                <span className="font-display font-black text-brand-green text-2xl">{RSD(product.happy_hour.price)}</span>
                <span className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${product.happy_hour.product_discount ? "bg-brand-green text-white" : product.happy_hour.store_wide ? "bg-brand-red text-white" : "bg-amber-400 text-slate-900"}`}>
                  {product.happy_hour.product_discount
                    ? (product.happy_hour.discount_type === "PERCENTAGE" ? `-${product.happy_hour.discount_value}%` : `-${RSD(product.happy_hour.discount_value)}`)
                    : product.happy_hour.store_wide ? `-${product.happy_hour.discount_percent}%` : "Happy Hour"}
                </span>
              </div>
            ) : (
              <div className="font-display font-black text-brand-red text-lg mt-2">{RSD(product.price)}</div>
            )}

            {(product.modifier_groups || []).map((g) => {
              const isErr = errorGroup === g.id;
              return (
                <div key={g.id} ref={(el) => (groupRefs.current[g.id] = el)}
                  data-testid={`group-${g.id}`}
                  className={`mt-4 rounded-2xl transition-colors ${isErr ? "ring-2 ring-brand-red bg-brand-red/5 p-3 -mx-0" : ""}`}>
                  <div className="mb-2.5">
                    <h3 className="font-display font-black text-base uppercase tracking-wide text-slate-900">{L(g, locale)}</h3>
                    <span data-testid={`group-rule-${g.id}`}
                      className={`text-[11px] font-bold uppercase tracking-wide ${g.required ? "text-brand-red" : "text-slate-400"}`}>
                      {ruleLabel(g, t)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(g.options || []).map((o) => {
                      const on = (selected[g.id] || []).includes(o.id);
                      const off = o.available === false;
                      return (
                        <button key={o.id} data-testid={`opt-${o.id}`} disabled={off} onClick={() => !off && toggle(g, o.id)}
                          aria-pressed={on}
                          className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-2 transition-colors ${off ? "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed" : on ? "border-brand-red bg-brand-red/10" : "border-slate-200 bg-white"}`}>
                          <span className="flex items-center gap-2.5 min-w-0">
                            <span className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${on ? "border-brand-red bg-brand-red text-white" : "border-slate-300"}`}>
                              {on && <Check size={13} strokeWidth={3} />}
                            </span>
                            <span className={`font-semibold truncate ${off ? "text-slate-400" : on ? "text-brand-red" : "text-slate-800"}`}>{L(o, locale)}</span>
                          </span>
                          {off
                            ? <span className="text-[10px] font-bold uppercase text-slate-400 shrink-0">{t("menu.unavailable", "Nedostupno")}</span>
                            : <span className={`text-sm shrink-0 ${on ? "text-brand-red font-bold" : "text-slate-500"}`}>{o.price > 0 ? `+${RSD(o.price)}` : ""}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {(product.extras || []).length > 0 && (
              <div className="mt-4">
                <div className="mb-2.5">
                  <h3 className="font-display font-black text-base uppercase tracking-wide text-slate-900">{t("menu.extras", "Dodaci")}</h3>
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("menu.optional", "Opciono")}</span>
                </div>
                <div className="space-y-2">
                  {product.extras.map((o) => {
                    const on = (selected.__extras || []).includes(o.id);
                    return (
                      <button key={o.id} data-testid={`extra-${o.id}`} onClick={() => toggleExtra(o.id)} aria-pressed={on}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-2 transition-colors ${on ? "border-brand-green bg-brand-green/10" : "border-slate-200 bg-white"}`}>
                        <span className="flex items-center gap-2.5 min-w-0">
                          <span className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${on ? "border-brand-green bg-brand-green text-white" : "border-slate-300"}`}>
                            {on && <Check size={13} strokeWidth={3} />}
                          </span>
                          <span className={`font-semibold truncate ${on ? "text-brand-green" : "text-slate-800"}`}>{L(o, locale)}</span>
                        </span>
                        <span className="text-sm text-slate-500 shrink-0">+{RSD(o.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-brand-red/5 border-2 border-brand-red/15 p-4" data-testid="person-field">
              <label className="font-display font-black text-lg text-brand-red">{t("item.for_label", "Za?")}</label>
              <p className="text-xs text-slate-500 mb-2">{t("item.for_hint", "Za koga je ovaj proizvod? (opciono)")}</p>
              <input data-testid="product-person" value={person} maxLength={40} onChange={(e) => setPerson(e.target.value)}
                placeholder={t("item.for_ph", "npr. Marko")} className="w-full px-4 py-3 rounded-xl bg-white border border-brand-red/20 font-semibold outline-none focus:ring-2 focus:ring-brand-red" />
            </div>
            <div className="mt-3">
              <label className="font-bold text-sm text-slate-700">{t("item.note_label", "Napomena")}</label>
              <input data-testid="product-note" value={note} maxLength={250} onChange={(e) => setNote(e.target.value)}
                placeholder={t("item.note_ph", "npr. bez luka")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-slate-100 outline-none focus:ring-2 focus:ring-brand-red" />
            </div>

            {/* Configuration summary */}
            {!impossible && (
              <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid="product-summary">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">{t("menu.your_choice", "Vaš izbor")}</p>
                <p className="font-display font-bold text-slate-900">{qty > 1 ? `${qty} × ` : ""}{L(product, locale)}</p>
                {selectedOpts.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {selectedOpts.map((o) => (
                      <li key={o.id} className="text-sm text-slate-500 flex items-center gap-1.5"><span className="text-brand-red">•</span> {L(o, locale)}</li>
                    ))}
                  </ul>
                )}
                {note && <p className="text-xs text-slate-400 italic mt-1">"{note}"</p>}
                <p className="font-display font-black text-brand-red mt-2">{RSD(unit * qty)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sticky action area */}
        <div className="border-t bg-white p-4 flex items-center gap-3">
          <div className="flex items-center gap-3 bg-slate-100 rounded-full px-2 py-1.5">
            <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full bg-white flex items-center justify-center"><Minus size={16} /></button>
            <span className="font-display font-black w-5 text-center">{qty}</span>
            <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center"><Plus size={16} /></button>
          </div>
          <button data-testid="add-to-cart" disabled={impossible} onClick={add} className="flex-1 bg-brand-red text-white rounded-full py-3.5 font-display font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
            {impossible ? t("menu.currently_unavailable", "Trenutno nedostupno") : `${t("menu.add_to_cart", "Dodaj u korpu")} · ${RSD(unit * qty)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
